
  import { defineComponent, onMounted, ref, computed, watch } from 'vue';
  import { ErrorMessage, Field, Form } from 'vee-validate';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRouter } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import * as Yup from 'yup';
  import Multiselect from '@vueform/multiselect';
  import { StatusNotesMap } from './status-notes.enum';
  import { TradeStatusMap } from './status-map.enum';

  export default defineComponent({
    name: 'registered-trades-creating',
    components: {
      ErrorMessage,
      Field,
      Form,
      Multiselect,
    },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const submitButton = ref<HTMLButtonElement | null>(null);
      const { can } = useAbility();
      const selectedArea = ref<number>(0);
      const selectedPropertyType = ref<number>(0);
      const descriptionId = ref(0);
      const descriptionAr = ref('');
      const descriptionEn = ref('');
      const notesEn = ref('');
      const notesAr = ref('');
      const size = ref(null);
      const price = ref();
      const pricePerM2 = ref(0);
      const percentage = ref('100');
      const statusNotesId = ref(0);
      const status = ref(1);
      const statusMap = TradeStatusMap;
      const statusNoteMap = StatusNotesMap;

      const selectedNote = ref<number>(0);
      selectedNote.value = 23;

      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const goBack = () => {
        router.back();
      };

      watch(
        () => status,
        (newStatus) => {
          if (newStatus.value === 1) {
            statusNotesId.value = null as any;
          }
        },
        { immediate: true }
      );

      const registeredTradeSchema = Yup.object().shape({
        transactionDate: Yup.date()
          .required(() => translate('TRANSACTION_DATE_TIME_IS_REQUIRED_FIELD'))
          .label('transactionDate'),
        size: Yup.number()
          .required(() => translate('SIZE_IS_REQUIRED_FIELD'))
          .label('size'),
        // price: Yup.string()
        //   .required(() => translate('PRICE_IS_REQUIRED_FIELD'))
        //   .label('price'),
        pricePerM2: Yup.number()
          .required(() => translate('PRICE_PER_M2_IS_REQUIRED_FIELD'))
          .label('pricePerM2'),
      });

      const updateDesc = async (values) => {
        debugger;
        const selectedDesc = registeredDescs.value.find(
          (desc) => desc.id == values
        );
        descriptionId.value = Number(selectedDesc?.id);
        descriptionAr.value = selectedDesc?.titleAr;
        descriptionEn.value = selectedDesc?.titleEn;
      };

      const updateNotes = async (values) => {
        const selectedNotes = registeredNotes.value.find(
          (desc) => desc.id == values
        );
        notesAr.value = selectedNotes?.titleAr;
        notesEn.value = selectedNotes?.titleEn;
      };

      const onSubmitCreate = async (values) => {
        if (selectedArea.value == 0 || selectedPropertyType.value == 0) return;

        if (submitButton.value) {
          if (values.description) {
            values.descriptionId = values.description;
            delete values.description;
          }

          // eslint-disable-next-line
        submitButton.value!.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }

        values.areaId = selectedArea.value;
        values.propertyTypeId = selectedPropertyType.value;
        if (values.price) {
          const numericValue = values.price.replace(/[^0-9.]/g, '');
          values.price = Number(numericValue);
        }
        values.pricePerM2 = Number(values.pricePerM2);
        values.size = Number(values.size);
        delete values.description;
        delete values.notes;
        if (status.value) values.status = status.value;

        if (statusNotesId.value && status.value === 1)
          values.statusNotesId = statusNotesId.value;

        await store.dispatch(Actions.CREATE_REGISTERED_TRADE, values);
        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          //Deactivate indicator
          submitButton.value?.removeAttribute('data-kt-indicator');
          // eslint-disable-next-line
        submitButton.value!.disabled = false;
        } else {
          Swal.fire({
            text: translate('SUCCESSFULLY_CREATED_REGISTERED_TRADE'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            router.push({ name: 'registered-trades-listing' });
          });
        }
      };

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('registeredTradesCreating'), [
          translate('appraisals'),
        ]);
      });

      await store.dispatch(Actions.GET_ALL_AREAS_LIST, {
        type: 'registeredTrades',
      });
      await store.dispatch(Actions.GET_ALL_PROPERTY_TYPE, {
        type: 'registeredTrades',
      });

      await store.dispatch(Actions.GET_REGISTERED_TRADES_ASSETS, 6);
      await store.dispatch(Actions.GET_REGISTERED_TRADES_ASSETS, 7);
      const registeredNotes = computed(() => store.getters.getRegisteredNotes);

      updateNotes(23);
      const registeredDescs = computed(() => store.getters.getRegisteredDesc);
      const updatePricePerSqrM = async () => {
        if (size.value && price.value) {
          const numericValue = price.value.replace(/[^0-9.]/g, '');
          pricePerM2.value = Number(
            (Number(numericValue) / Number(size.value)).toFixed(2)
          );
        }
      };

      const onPriceInput = async () => {
        const input = price.value.replace(/,/g, ''); // Remove commas for parsing
        const numericValue = parseFloat(input);

        if (!isNaN(numericValue)) {
          price.value = new Intl.NumberFormat('en-US').format(numericValue); // Format for display
        }
      };
      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        percentage,
        onPriceInput,
        areas: computed(() => store.getters.allListAreas),
        propertyTypes: computed(() => store.getters.propertyTypesAllList),
        onSubmitCreate,
        submitButton,
        translate,
        registeredTradeSchema,
        goBack,
        can,
        selectedArea,
        selectedPropertyType,
        selectedNote,
        notesAr,
        notesEn,
        descriptionId,
        descriptionAr,
        descriptionEn,
        updateNotes,
        updateDesc,
        registeredDescs,
        registeredNotes,
        size,
        price,
        pricePerM2,
        updatePricePerSqrM,
        statusNotesId,
        status,
        statusMap,
        statusNoteMap,
      };
    },
  });
